/**
 * Which Guest Management Link a Guest Checkout belongs to, remembered on the studio's origin for
 * 24 hours. The payment provider returns to `/payment-return?id={checkoutId}` without the link
 * token, so the return page can reopen the booking only in the browser that started the payment.
 */
const STORAGE_KEY = 'fitro.guestPaymentReturns'
const TTL_MS = 24 * 60 * 60 * 1000
// How long after coming back from the provider the booking page treats a pending payment as being checked.
const RETURN_CHECK_MS = 10 * 60 * 1000

type Entry = {token: string; expiresAt: number; returnedAt?: number}

function readLive(): Record<string, Entry> {
    try {
        const value: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}')
        if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
        const now = Date.now()
        return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter((pair): pair is [string, Entry] => {
            const entry = pair[1] as Partial<Entry> | null
            return typeof entry?.token === 'string' && typeof entry.expiresAt === 'number' && entry.expiresAt > now
        }))
    } catch {
        return {}
    }
}

function write(entries: Record<string, Entry>): void {
    try {
        if (Object.keys(entries).length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
        else window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        // Storage can be unavailable (private mode); the return page then points to the email link.
    }
}

export function rememberGuestPaymentReturn(checkoutId: string, token: string): void {
    write({...readLive(), [checkoutId]: {token, expiresAt: Date.now() + TTL_MS}})
}

/**
 * The link token for this checkout, noting that the guest has just come back from the provider.
 * The entry stays until it expires, so going back to the provider and returning again still works.
 */
export function returnFromGuestPayment(checkoutId: string): string | null {
    const entries = readLive()
    const entry = entries[checkoutId]
    if (entry) entries[checkoutId] = {...entry, returnedAt: Date.now()}
    write(entries)
    return entry?.token ?? null
}

/**
 * When this browser stops treating the booking's pending payment as being checked: a few minutes
 * after the guest came back from the provider, or `null` when that was not just now.
 */
export function guestPaymentCheckEndsAt(token: string): number | null {
    const now = Date.now()
    const endsAt = Object.values(readLive())
        .filter((entry) => entry.token === token && entry.returnedAt !== undefined)
        .map((entry) => (entry.returnedAt ?? 0) + RETURN_CHECK_MS)
        .filter((end) => end > now)
    return endsAt.length ? Math.max(...endsAt) : null
}

/** Forgets the booking's link once its payment is settled; the email keeps it for the guest. */
export function forgetGuestPaymentReturn(token: string): void {
    write(Object.fromEntries(Object.entries(readLive()).filter(([, entry]) => entry.token !== token)))
}

/** Drops expired links, so an abandoned payment does not leave its token in the browser. */
export function clearExpiredGuestPaymentReturns(): void {
    write(readLive())
}
